import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MessageCircle, Heart, Copy, Check } from 'lucide-react';

const ContactSection = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '50px 0px', // Start animation when component is 50px before entering viewport
  });

  const [copiedText, setCopiedText] = useState('');

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };



  const contactTeam = [
    {
      name: "נילי שטול טראורינג",
      role: "הדרכה וליווי, יוצרת סדנאות להגשמה",
      phone: "0542468486",
      email: "lahlombegadol@gmail.com",
      color: "from-dream-teal to-dream-warm-brown"
    },
    {
      name: "גלית תמר",
      role: "מנחת סדנאות לוחות חזון ומטפלת באומנות",
      phone: "0523433399",
      email: "galittamar@gmail.com",
      color: "from-dream-warm-brown to-dream-pink-beige"
    }
  ];

  return (
    <section id="contact" className="section-padding bg-gradient-to-br from-dream-warm-brown via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-40 h-40 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-15"></div>
        <div className="absolute bottom-20 right-20 w-32 h-32 bg-gradient-to-r from-dream-pink-beige to-dream-soft-beige rounded-full blur-2xl opacity-15"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-lg opacity-10"></div>
      </div>

      {/* Subtle floating elements */}
      <motion.div
        className="absolute top-32 right-20 text-dream-teal opacity-30"
        animate={{ y: [-10, 10, -10] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-3 h-3 bg-current rounded-full"></div>
      </motion.div>
      <motion.div
        className="absolute bottom-40 left-20 text-dream-pink-beige opacity-30"
        animate={{ y: [10, -10, 10] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      >
        <Heart size={20} fill="currentColor" />
      </motion.div>

      <motion.div
        ref={ref}
        className="container-width relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            מוכנים <span className="gradient-text">לחלום בגדול</span>?
          </h2>
          <p className="text-xl text-dream-light-beige max-w-3xl mx-auto">
            הזמינו עכשיו את הסדנה הייחודית לארגון שלכם 
            והעניקו לצוות שלכם חוויה בלתי נשכחת
          </p>
        </motion.div>

        {/* Contact Team */}
        <div className="max-w-5xl mx-auto">
          <motion.div variants={itemVariants}>
            <h3 className="text-2xl font-bold text-white mb-12 text-center">צרו איתנו קשר</h3>
            
            {/* Team Members */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {contactTeam.map((member, index) => (
                <motion.div
                  key={index}
                  className="glass-effect p-8 rounded-2xl hover:bg-white hover:bg-opacity-20 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  {/* Member Header */}
                  <div className="text-center mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-r ${member.color} rounded-full flex items-center justify-center text-white mx-auto mb-4`}>
                      <span className="text-2xl font-bold">{member.name.charAt(0)}</span>
                    </div>
                    <h4 className="font-bold text-white text-xl mb-2">{member.name}</h4>
                    <p className="text-dream-light-beige text-sm">{member.role}</p>
                  </div>

                  {/* Contact Methods */}
                  <div className="space-y-4">
                    {/* Phone */}
                    <div className="relative">
                      <a 
                        href={`tel:${member.phone}`}
                        className="flex items-center justify-between p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-all duration-300 cursor-pointer block"
                      >
                        <div className="flex items-center gap-3">
                          <Phone size={20} className="text-dream-teal" />
                          <div>
                            <span className="text-white font-semibold text-lg hover:text-dream-teal transition-colors">
                              {member.phone}
                            </span>
                            <p className="text-dream-light-beige text-xs">לחצו להתקשר</p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            copyToClipboard(member.phone);
                          }}
                          className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-300"
                          title="העתק מספר טלפון"
                        >
                          {copiedText === member.phone ? (
                            <Check size={16} className="text-green-400" />
                          ) : (
                            <Copy size={16} className="text-dream-light-beige hover:text-white" />
                          )}
                        </button>
                      </a>
                    </div>

                    {/* Email */}
                    <div className="relative">
                      <a 
                        href={`mailto:${member.email}`}
                        className="flex items-center justify-between p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-all duration-300 cursor-pointer block"
                      >
                        <div className="flex items-center gap-3">
                          <Mail size={20} className="text-dream-warm-brown" />
                          <div>
                            <span className="text-white font-semibold text-sm hover:text-dream-teal transition-colors break-all">
                              {member.email}
                            </span>
                            <p className="text-dream-light-beige text-xs">לחצו לשליחת מייל</p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            copyToClipboard(member.email);
                          }}
                          className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-300"
                          title="העתק כתובת מייל"
                        >
                          {copiedText === member.email ? (
                            <Check size={16} className="text-green-400" />
                          ) : (
                            <Copy size={16} className="text-dream-light-beige hover:text-white" />
                          )}
                        </button>
                      </a>
                    </div>

                    {/* WhatsApp */}
                    <a 
                      href={`https://wa.me/972${member.phone.substring(1)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-all duration-300 cursor-pointer block"
                    >
                      <MessageCircle size={20} className="text-green-400" />
                      <div>
                        <span className="text-white font-semibold hover:text-dream-teal transition-colors">WhatsApp</span>
                        <p className="text-dream-light-beige text-xs">לחצו לשליחת הודעה</p>
                      </div>
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default ContactSection;
